import React from 'react';
import { Alert, Card, Slider } from 'antd';

import { TrackConf } from '../../../../lib/App';

export enum TRACK_LEVEL {
  TRACK_EVERYTHING = 0,
  TRACK_USING_SAMPLE_RATE = 1,
  TRACK_NOTHING = 2,
}

type MarksMap = {
  [key: number]: string;
};

const MIN_MARK = 10;
export const TRACK_CONF_MIN_LIMIT = 0.99;

export const marks: MarksMap = {
  [MIN_MARK]: '1%',
  20: '3%',
  30: '5%',
  40: '10%',
  50: '25%',
  60: '50%',
  70: '75%',
  80: '90%',
  100: '100%',
};

export const getKeyFromValue = (dict: MarksMap, value: number) => {
  const formattedValue = value + '%';
  for (let key in dict) {
    if (dict[key] === formattedValue) {
      return key;
    }
  }
};

export const extractInteger = (slideValue: string) =>
  parseInt(slideValue.match(/\w+/)![0], 10);

export interface RolloutTrackConfProps {
  trackConf: TrackConf;
  onChange: (trackConf: TrackConf) => void;
  disabled?: boolean;
}

export interface RolloutTrackConfState {
  showWarningMessage: boolean;
  sampleRatePercentage: number;
  toDisplay: number;
}

export class RolloutTrackConf extends React.Component<
  RolloutTrackConfProps,
  RolloutTrackConfState
> {
  constructor(props) {
    super(props);
    const { trackConf } = this.props;
    const { showWarningMessage, sampleRatePercentage, toDisplay } =
      this.updateSliderValue(trackConf);
    this.state = { showWarningMessage, sampleRatePercentage, toDisplay };
  }

  componentDidUpdate(prevProps) {
    const { trackConf } = this.props;

    if (trackConf && prevProps.trackConf.limit != trackConf.limit) {
      const { showWarningMessage, sampleRatePercentage, toDisplay } =
        this.updateSliderValue(trackConf);
      this.setState({
        showWarningMessage: showWarningMessage,
        sampleRatePercentage: sampleRatePercentage,
        toDisplay: toDisplay,
      });
    }
  }

  updateSliderValue = (trackConf: TrackConf) => {
    const cloneTrackConf = { ...trackConf };

    if (cloneTrackConf.limit === 1) {
      cloneTrackConf.limit = TRACK_CONF_MIN_LIMIT;
      this.props.onChange({
        mode: TRACK_LEVEL.TRACK_USING_SAMPLE_RATE,
        limit: TRACK_CONF_MIN_LIMIT,
      });
    }

    const sampleRatePercentage = cloneTrackConf.limit
      ? Math.round((1 - cloneTrackConf.limit) * 100)
      : 100;

    const toDisplay = extractInteger(
      marks[getKeyFromValue(marks, sampleRatePercentage)!],
    );
    const showWarningMessage = sampleRatePercentage >= 25 ? true : false;
    return { showWarningMessage, sampleRatePercentage, toDisplay };
  };

  handleTrackSliderChange = (
    sampleRateSlideValueInput: number | [number, number],
  ): void => {
    let sampleRateSlideValue: number;
    if (Array.isArray(sampleRateSlideValueInput)) {
      sampleRateSlideValue = sampleRateSlideValueInput[0];
    } else {
      sampleRateSlideValue = sampleRateSlideValueInput;
    }
    const toDisplay = extractInteger(marks[sampleRateSlideValue]);
    const showWarningMessage = toDisplay >= 25 ? true : false;
    const sampleRateRatio = parseFloat((toDisplay / 100).toFixed(2));
    let trackConfLimit = parseFloat((1 - sampleRateRatio).toFixed(2));

    let mode: number;
    switch (sampleRateSlideValue) {
      case 0:
        mode = TRACK_LEVEL.TRACK_NOTHING;
        trackConfLimit = 1;
        break;
      case 100:
        mode = TRACK_LEVEL.TRACK_EVERYTHING;
        trackConfLimit = 0;
        break;
      default:
        mode = TRACK_LEVEL.TRACK_USING_SAMPLE_RATE;
    }

    this.setState({
      showWarningMessage,
      sampleRatePercentage: sampleRateSlideValue,
      toDisplay,
    });
    this.props.onChange({
      mode,
      limit: trackConfLimit,
    });
  };

  createWarningMessage = (): React.ReactNode => {
    const { toDisplay: trackPercent } = this.state;

    const rf = (
      <>
        <div style={{ display: 'inline-flex' }}>
          If{'\u00a0'}
          <span style={{ color: '#f50', fontWeight: 'bold' }}>
            {trackPercent}%
          </span>
          {'\u00a0'}rollout events are not required in the eventlog table,
          please limit tracking to between{'\u00a0'}
          <span style={{ color: '#008000', fontWeight: 'bold' }}>0%</span>
          {'\u00a0'}to{'\u00a0'}
          <span style={{ color: '#008000', fontWeight: 'bold' }}>10%</span>.
        </div>
        <br />
        <br />
        <div>Note:</div>
        <div>
          - Events generated from experiments on this variable will be tracked
          at {trackPercent}%.
        </div>
        <div>
          - Increase rollout event tracking % for debugging at any time if
          required in less than 5 min.
        </div>
      </>
    );
    return rf;
  };

  render() {
    const { showWarningMessage, sampleRatePercentage, toDisplay } = this.state;
    const { disabled } = this.props;

    const formattedSliderValue = extractInteger(
      getKeyFromValue(marks, toDisplay)!,
    );

    return (
      <Card
        title={`Rollout Event Tracking at ${
          sampleRatePercentage === 0 ? 0 : toDisplay
        }%`}
      >
        <Slider
          min={MIN_MARK}
          marks={marks}
          defaultValue={sampleRatePercentage === 0 ? 0 : formattedSliderValue}
          value={sampleRatePercentage === 0 ? 0 : formattedSliderValue}
          onChange={this.handleTrackSliderChange}
          tooltip={{ open: false }}
          step={null}
          disabled={disabled}
        />
        <br />
        {showWarningMessage ? (
          <Alert
            message="Warning"
            description={this.createWarningMessage()}
            type="warning"
            showIcon
            closable
          />
        ) : null}
      </Card>
    );
  }
}
